import React, { useMemo } from 'react'
import { Form } from 'semantic-ui-react'
import styled from 'styled-components'
import { VERIFICATION_METHOD_OPTIONS, needsManagerApproval, needsVerificationInspection } from './maintenance-utils'

const Hint = styled.div`
    color: #6b7280;
    font-size: 12px;
    margin: -8px 0 12px;
`

/**
 * Verification settings shared by the maintenance task form, the schedule form and the task detail (EAUDITOR-410):
 * method (inspection / manager approval / both), verification template, verifier + separation of duty, pass mark,
 * approver, close only after verification. Rendered while "Verification required" is on.
 * Props: form, set(field, value), templateOptions, userOptions (no user pickers when empty), errors, disabled.
 */
export const VerificationSettings = ({ form, set, templateOptions = [], userOptions = [], errors = {}, disabled = false }) => {
    const method = Number(form.VerificationMethod) || 1
    const inspection = needsVerificationInspection(method)
    const approval = needsManagerApproval(method)
    const err = field => (errors[field] ? { content: errors[field] } : undefined)

    const verifierOptions = useMemo(
        () => [{ key: 'none', value: '', text: form.SeparationOfDuty ? 'Task creator (default)' : 'The technician (default)' }, ...userOptions],
        [userOptions, form.SeparationOfDuty],
    )
    const approverOptions = useMemo(() => [{ key: 'none', value: '', text: 'Any company admin' }, ...userOptions], [userOptions])

    return (
        <>
            <Form.Dropdown
                label="How is the work verified"
                selection
                disabled={disabled}
                options={VERIFICATION_METHOD_OPTIONS}
                value={method}
                onChange={(e, { value }) => set('VerificationMethod', value)}
            />
            {inspection && (
                <>
                    <Form.Dropdown
                        label="Verification template"
                        selection
                        search
                        disabled={disabled}
                        placeholder="Created automatically when the work is completed"
                        options={templateOptions}
                        value={form.VerificationTemplateId}
                        onChange={(e, { value }) => set('VerificationTemplateId', value)}
                        error={err('VerificationTemplateId')}
                    />
                    <Form.Group widths="equal">
                        {userOptions.length > 0 && (
                            <Form.Dropdown
                                label="Verified by"
                                selection
                                search
                                disabled={disabled}
                                options={verifierOptions}
                                value={form.VerifierUserId}
                                onChange={(e, { value }) => set('VerifierUserId', value)}
                                error={err('VerifierUserId')}
                            />
                        )}
                        <Form.Input
                            label="Pass mark (%)"
                            type="number"
                            min={0}
                            max={100}
                            placeholder="Optional"
                            disabled={disabled}
                            value={form.VerificationPassMark}
                            onChange={(e, { value }) => set('VerificationPassMark', value)}
                            error={err('VerificationPassMark')}
                        />
                    </Form.Group>
                    <Form.Checkbox
                        label="Separation of duty - the technician never does the verification inspection"
                        disabled={disabled}
                        checked={!!form.SeparationOfDuty}
                        onChange={(e, { checked }) => set('SeparationOfDuty', checked)}
                    />
                    <Hint>
                        The verification fails when an answer is a finding (for example NO / FAIL / Needs Repair rules) or, for scored
                        checklists, when the score is below the pass mark. The work then goes back to the technician.
                    </Hint>
                </>
            )}
            {approval && (
                <>
                    {userOptions.length > 0 && (
                        <Form.Dropdown
                            label="Approved by"
                            selection
                            search
                            disabled={disabled}
                            options={approverOptions}
                            value={form.ApproverUserId}
                            onChange={(e, { value }) => set('ApproverUserId', value)}
                            error={err('ApproverUserId')}
                        />
                    )}
                    <Hint>
                        Company admins can always approve. The technician can never approve their own work. A rejection sends the
                        work back to the technician{method === 3 ? ' and needs a new verification inspection' : ''}.
                    </Hint>
                </>
            )}
            <Form.Checkbox
                label="Close the task only after the verification is complete"
                disabled={disabled}
                checked={!!form.CloseOnlyAfterVerification}
                onChange={(e, { checked }) => set('CloseOnlyAfterVerification', checked)}
            />
        </>
    )
}
